import { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';
import { Moment } from 'moment';
import { ParsedQuery } from 'query-string';

export type EntityID = string;

export type EntityBase = {
  id: EntityID;
  deleted?: number;
  name?: string;
  $fromSocket?: boolean;
  // extended
  $toString: string;
  $isDeleted: boolean;
  $isPopulated: boolean;
};

export type Entity = EntityBase & {
  user: EntityID;
  createdBy: EntityID;
  updatedBy: EntityID;
  updatedAt: string;
  createdAt: string;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/setting.js
export type Setting = {
  reminder: {
    // this setting controls how Luma will handle a patient who has multiple appointments
    // within one day. for example a patient may have an appointment at 9am and 10am.
    // should Luma send a reminder for all appointments in the day ('multipleRemindersPerDay')
    // or only for the first in the day ('onlySendEarliest')?
    // Considerations
    // * for onlySendEarliest, the first appointment in the day for whom all
    // resources have the reminder service enabled will be considered the "anchor" appointment.
    // /followup/ Any appointments that have a disabled resource will not be considered (e.g. if the
    // the 9am appointment was for a disabled provider, it would not be eligible to be the anchor).
    // * if onlySendEarliest is set, and a patient replies to a reminder (to confirm, most likely)
    // but at some point between that reminder and the next the first appointment of the day is
    // deleted or removed, the reminder sequence is not recalculated, since one reminder for that
    // day has already been sent and any subsequent messaing to the patient would likely cause
    // confusion
    multipleRemindersPerDay: 'onlySendEarliest' | 'multipleRemindersPerDay';
    // when operating at multipleRemindersPerDay = onlySendEarliest, should we
    // only count appointments whose "type" has reminders enabled? this is needed
    // because if the first appointment in a day had reminders disabled,
    // all reminders would end up skipped. Since clients got used to that behavior,
    // we're creating this new setting to only consider "multiple appointments"
    // if all those appointments' types have reminders enabled. #5b6186
    onlySendEarliestForApptTypesWithRemindersEnabled?: boolean;
    // should we only show appointments in common contexts squigglies such as appointmentsListInDay
    // with resources with reminders setting enabled? some customers wanted the behavior to include all
    // appointments regardless of resources with reminders enabled or disabled. This setting will
    // control whether we show all appointments or filter out with only resources with reminders enabled
    // #zkk2ve
    multipleRemindersPerDayShouldOnlyConsiderResourcesWithRemindersEnabledInSquiggly?: boolean;
    // when operating at multipleRemindersPerDay = onlySendEarliest, if a patient
    // replies, this setting will used to override if the patient replies
    // when they have multiple reminders in the day
    // this applies only to the SMS channel. if this is set
    // cancelOrConfirmAllAppointmentsInDay will be ignored
    multipleRemindersPerDayRepliesOverride?: 'disabled' | 'allowed' | 'confirmationOnly' | 'none';
    schedule: 168 | 96 | 48;
    enabled?: boolean;
    onConfirmation?: 'skipFurtherReminders' | 'keepLastReminder';
    // if onConfirmation == keepLastReminder, the user has the option
    // to use the repliesForLastReminder key instead of the default
    // key (e.g. so the last reminder can be just a reminder, and
    // not an actionable "reply yes to confirm")
    repliesForLastReminder: 'allowed' | 'confirmationOnly' | 'none';
    replies: 'allowed' | 'confirmationOnly' | 'none';
    // the types of reminders this account wants to send out
    // if a patient is encountered with a type that
    // isn't enabled, reminders will not be sent to
    // that patient
    enabledChannels: ('sms' | 'voice' | 'email' | 'fax' | 'whatsapp')[];
    // number of hours to treat a cancellation as a no-show
    // if it's set to zero, then all cancellations are cancellations
    // if it's > 0, then cancellations inside of that time to the
    // appointment are considered a no-show and sent the
    // patientReminderCancellationNoShow message
    // otherwise, they're sent patientReminderCancellationThanks
    treatCancellationsAsNoShows: number;
    // when false if this && cancellation.enabled is true
    // the patient gets a follow up text that asks if they
    // want to join the waitlist after they cancel
    offerToJoinWaitlistOnCancellation: 'disabled' | 'from-reminder' | 'from-integrator' | 'from-both';
    // window at which to schedule reminders
    // meaning we won't send reminders earlier than 0800
    // and never later than 2100, in the local timezone
    // NOTE: this also impacts the times that cancellations
    // are allowed to send. for legacy reasons, it's inside
    // of the reminder settings block
    schedulableWindow: {
      earliest: number;
      latest: number;
    };
    // if true, will append a URL to the end of the 'thanks for cancelling'
    // texts that gives the patient the option to book an appointment
    allowSelfSchedule?: boolean;
    // if true, if a patient replies with "resched" witin their reply to an
    // appointment reminder, we will reply with a list of slots the patient
    // can reschedule with and take them through an NLP driven flow to rebook
    // their appointment
    allowSelfReschedule?: boolean;
    // if set, will filter reschedule availabilities based on the selected sources
    allowSelfRescheduleAvailabilityFilter?: 'appointment-cancellation' | 'ui' | 'referral' | 'integrator';
    // if allowSelfReschedule is set to true, how long should the patient be
    // allowed to reschedule for? by default 30 minutes. if you wish for
    // the reschedule to be valid until the next appointment reminder is sent
    // then set this to some absurtly high number like 10,080 minutes
    allowSelfRescheduleExpiry?: number;
    // when true, any reminders that would have been scheduled on a sunday
    // are moved back one day to saturday at the exact same time of day
    rescheduleSundayToSaturday?: boolean;
    // when true, any subsequent reminders for an appointment that
    // would have been delivered as an SMS to a mobile number will be
    // converted to voice calls and delivered by robo-dial
    forceVoiceCallsForSmsNonResponders?: boolean;
    // when true, if a patient cancels or confirms an appt for
    // a day, all the appts in that day for that patient will
    // get cancelled or confirmed
    cancelOrConfirmAllAppointmentsInDay?: boolean;
    // when true, the setting above will cause the
    // cancellation of confirmation of only
    // those appointments in that day whose type is visible;
    // hidden appointment types will not be changed
    cancelOrConfirmAppointmentsInDayOnlyIfTypeVisible?: boolean;
    // setup:
    // - user.settings.settingsToOverrideFrom.facilities = ['cancelOrConfirmAllAppointmentsInDay']
    // - facilityA.settings.cancelOrConfirmAllAppointmentsInDay = true
    // - facilityC.settings.cancelOrConfirmAllAppointmentsInDay = true
    // - facilityB.settings.shouldRespectCancelOrConfirmAllAppointmentsInDay = false
    // flow: if patient cancels an appointment on facilityA/facilityC, it will not cancel on facilityB
    shouldRespectCancelOrConfirmAllAppointmentsInDay?: boolean;
    // when true, if a patient replies "confirm" after they reply
    // "cancel", we'll process it and send them a thanks and mark
    // the appointment as confirmed. if false, it'll block the patient
    // and tell them to call the practice
    allowConfirmAfterCancel?: boolean;
    // when true, if a patient replies "cancel" after they reply
    // "confirm", we'll process it and send them a thanks and mark
    // the appointment as confirmed. if false, it'll block the patient
    // and tell them to call the practice
    allowCancelAfterConfirm?: boolean;
    // when true, only "Yes" and "No" will be processed as confirm
    // or cancel. by default this is false so we also include things like
    // nope, yep, yeah, etc.
    strictConfirmCancelTextHandling?: boolean;
    // normally Luma will attempt to fit in a reminder if none can be
    // done by the schedule. this will disable the "at least one touch"
    // rule and not run the catchup / attemptReminderReschedule
    // logic
    disableCatchupReminders?: boolean;
    botResponses: {
      enabled?: boolean;
    };
    // reminder messages can be delayed forced to a specific hour within the day.
    // when enabled, the reminder schedule will be calculated based on the settings.reminder.schedule
    // and then within the day of the reminder, the hours and and minutes will be set to the time
    // specified in the forceMessageTime.until config. using this setting you cannot move the date
    // of the reminder, but you can use this to fix reminder messages to go out at 18h00m (for example)
    // the time shift is applied _before_ holiday rules, blackout time, the multipleRemindersPerDay setting
    // are applied; so in effect it pins the reminder.schedule
    forceMessageTime: {
      enabled?: boolean;
      // what time of day to delay messages to, expressed as a 24 hour clock
      until?: string;
    };
    // send reminders when they are scheduled to fall on holidays
    sendReminderOnHolidays: {
      enabled?: boolean;
    };
  };
  lumabot: {
    avatar?: FileUpload | string;
    enabled?: boolean;
    header?: string;
    includePoweredByLuma?: boolean;
    liveChat?: { enabled?: boolean };
    name?: string;
    placement?: 'right' | 'left';
    prompt?: string;
    theme?: ThemeOptions;
    isHideProviderDetails?: boolean;
  };
  // TODO type other settings apart from `reminder`
  userManagementVersion: any;
  session: any;
  welcome: any;
  telehealth: any;
  labs: any;
  outboundReferral: any;
  timezone: any;
  useTimezoneFrom: any;
  localization: any;
  scheduler: any;
  referral: any;
  cancellation: any;
  communication: any;
  appointment: any;
  feedback: any;
  followup: any;
  integrator: any;
  patients: any;
  reporting: any;
  waitingRoom: any;
  settingsToOverrideFrom: any;
  billing: any;
  patientForms: any;
  insurance: {
    verification: {
      enabled: boolean;
      hoursAheadToVerifyInsurance: number;
    };
  };
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/external-sources.ts
export enum ExternalSource {
  Gcalendar = 'gcalendar', // entered manually by users
  Successehs = 'successehs', // provides a durable ID
  Drchrono = 'drchrono', // drchrono provides a durable ID
  Dentrix = 'dentrix', // dentrix provides a durable ID
  Webpt = 'webpt', // created as LASTNAME + FIRSTNAME + YEAR + MONTH or MRN,
  Theraoffice = 'theraoffice', // theraoffic eprovides a durable ID,
  Mi7 = 'mi7', // mi7 provides two IDs, an internal one and a 3rd party system one
  Practicefusion = 'practicefusion', // if no "Patient Identifier", created as contactType+contactValue
  Advancedmd = 'advancedmd', // advanced MD has a chart ID and a patient ID so we may store either,
  Acomrapidpm = 'acomrapidpm',
  Kareo = 'kareo',
  Nextech = 'nextech',
  Mwtherapy = 'mwtherapy',
  Clinicient = 'clinicient',
  Carecloud = 'carecloud',
  Eclinicalmobile = 'eclinicalmobile',
  Duxware = 'duxware',
  Labretriever = 'labretriever',
  Optimispt = 'optimispt',
  Referral = 'referral',
  Recall = 'recall',
  Allscriptspm = 'allscriptspm',
  Lytec = 'lytec',
  Brightree = 'brightree',
  Fullslate = 'fullslate',
  Nuemd = 'nuemd',
  Centricityps = 'centricityps',
  Officeally = 'officeally',
  Greenwayintergy = 'greenwayintergy',
  Compulink = 'compulink',
  Adspm = 'adspm', // http://www.adsc.com/practice-management-software
  Dsnpm = 'dsnpm', // https://www.dsnsoft.com/
  Lumamock = 'lumamock', // a fake integration for testing
  Medicalmastermind = 'medicalmastermind',
  Meditouch = 'meditouch',
  Healthnautica = 'healthnautica',
  Ezemrx = 'ezemrx',
  Hl7 = 'hl7', // lumalink based HL7
  Amazingcharts = 'amazingcharts',
  Greenwayprimesuite = 'greenwayprimesuite',
  Raintree = 'raintree',
  Athenahealth = 'athenahealth',
  Revflow = 'revflow',
  Eclinicalworks10e = 'eclinicalworks10e',
  Hl7pickup = 'hl7pickup',
  Mindbody = 'mindbody',
  Eclinicalworkssql = 'eclinicalworkssql',
  Nextgen = 'nextgen',
  Practiceperfect = 'practiceperfect',
  Avimark = 'avimark',
  Clinix = 'clinix',
  Keymedical = 'keymedical',
  Mdoffice = 'mdoffice',
  Webedoctor = 'webedoctor',
  Emapm = 'emapm',
  Medinformatix = 'medinformatix',
  Imsgo = 'imsgo',
  Emds = 'emds',
  Allscriptsunity = 'allscriptsunity',
  Medevolve = 'medevolve',
  Caretracker = 'caretracker',
  Clearpractice = 'clearpractice',
  Valant = 'valant',
  Micromd = 'micromd',
  Systemedx = 'systemedx',
  Medicalmaster = 'medicalmaster', // note: this is different than medicalmastermind
  Athenamdp = 'athenamdp', // API based athena partner program
  Gmed = 'gmed', // ggastro, gpm, gmed,
  Roche = 'roche', // their injection management product,
  Onetouch = 'onetouch',
  Somnoware = 'somnoware',
  Managementplus = 'managementplus',
  Lumacare = 'lumacare', // simply the best
  Nextechfhir = 'nextechfhir',
  Curemd = 'curemd',
  Epic = 'epic',
  Phoenixortho = 'phoenixortho', // may also be known as openpracticesolutions
  Ezderm = 'ezderm',
  Ggastromobile = 'ggastromobile', // ggastro mobile app
  Epicconfirmationpickup = 'epicconfirmationpickup', // pickup confirmed files batch jobs
  Cerner = 'cerner',
  Allmeds = 'allmeds',
  Oncoemrfilepickup = 'oncoemrfilepickup',
  Imedicware = 'imedicware',
  Modmedfhir = 'modmedfhir',
  Clinux = 'clinux',
  Acuityscheduling = 'acuityscheduling',
  Medstreaming = 'medstreaming',
  ISalus = 'isalus',
  MeditechExpanse = 'meditechexpanse',
  OpenEMR = 'openemr',
  Genericfhir = 'genericfhir',
}

export type BillingVendorNotes = {
  validated: boolean;
  lastValidatedAt: string;
};

export type BillingVendor = Entity & {
  vendor: number;
  key: string;
  secret: string;
  notes?: BillingVendorNotes;
};

export type BillingCopay = Entity & {
  amount?: number;
  name: string;
  source: string;
  type: string;
};

export enum Language {
  Amharic = 'am', // currently (13-MARCH-2020) there is no config/messages.am.json file
  Arabic = 'ar',
  Bengali = 'bn',
  HakhaChin = 'cn',
  German = 'de',
  English = 'en',
  Spanish = 'es',
  Farsi = 'fa',
  Falam = 'fl',
  French = 'fr',
  Gujarati = 'gu',
  Hindi = 'hi',
  HaitianCreole = 'ht',
  Armenian = 'hy',
  Italian = 'it',
  Korean = 'ko',
  Marathi = 'mr',
  Burmese = 'my',
  Punjabi = 'pa',
  Polish = 'pl',
  Portuguese = 'pt',
  Russian = 'ru',
  Tagalog = 'tl',
  Ukrainian = 'uk',
  Urdu = 'ur',
  Vietnamese = 'vi',
  ChineseSimplified = 'zh',
  ZhS = 'zh-s', // currently (13-MARCH-2020), there is no zh-s language file.  Chinese Simplified is simply zh.
  ChineseTraditional = 'zh-t',
}

export type Role =
  | 'staff'
  | 'doctor'
  | 'patient'
  | 'widget'
  | 'subaccount'
  | 'admin'
  | 'referringProvider'
  | 'lumalink'
  | 'readFileUpload'
  | 'manager';

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/user.ts#L133
export enum ProvisioningEnum {
  Branding = 'branding',
  Broadcast = 'broadcast',
  Chat = 'chat',
  Feedback = 'feedback',
  Followup = 'followup',
  Form = 'form',
  Insurance = 'insurance',
  InsuranceVerification = 'insurance-verification',
  IntakeForm = 'intake-form',
  LumaBot = 'lumabot',
  LumaPay = 'luma-pay',
  NextGenPay = 'nextgen-pay',
  OutboundReferral = 'outbound-referral',
  PrequalForm = 'prequal-form',
  Recall = 'recall',
  Referral = 'referral',
  Reminder = 'reminder',
  Scheduler = 'scheduler',
  Telehealth = 'telehealth',
  Upload = 'upload',
  WaitingRoom = 'waiting-room',
  Waitlist = 'waitlist',
  GoogleAvailabilities = 'google-availabilities',
  DigitalCallDeflection = 'digital-call-deflection',
  SchedulerAssist = 'scheduler-assist',
  QueueManager = 'queue-manager',
}

export enum RolloutEnum {
  SparkManagement = 'spark-management',
  AppointmentChecklist = 'appointment-checklist',
  AdvancedSchedulerOptions = 'advanced-scheduler-options',
  CCDA = 'ccda',
  ChatInternalLinks = 'chatInternalLinks',
  ChecklistTemplate = 'checklist-template',
  Cobranding = 'cobranding',
  CustomFormsV2 = 'custom-forms-v2',
  Docusign = 'docusign',
  OmniChannelRetries = 'omnichannel-retries',
  EHRIntegrators = 'ehr-integrators',
  DesignatedNumbers = 'designated-numbers',
  EmbedBranding = 'embed-branding',
  // NOTE is this supposed to be a rollout?
  // https://github.com/lumahealthhq/web-app/pull/4162
  FormBuilder = 'formBuilder',
  FormBuilder2o0 = 'form-builder-2.0',
  InsuranceEligibility = 'insurance-eligibility',
  Journey = 'journey',
  Labs = 'labs',
  LinkedForms = 'linked-forms',
  LumaBotLiveChat = 'lumabot-livechat',
  LumaBotPoweredByLuma = 'lumabot-poweredbyluma',
  LumaBotV1 = 'lumabot-v1',
  LumaBotJumpV2 = 'lumabot-jump-v2',
  LumaBotSquigglies = 'lumabot-squigglies',
  MessagingActivity = 'messaging-activity',
  OldBroadcasts = 'old-broadcasts',
  PatientMerge = 'patient-merge',
  Reports = 'reports',
  SchedulerV2 = 'schedulerV2',
  SettingsV2 = 'settings-v2',
  FaxTransform = 'fax-transform',
  ScheduleView = 'schedule-view',
  SchedulerAssist = 'scheduler-assist',
  ThirdPartyIntegration = 'third-party-integration',
  WaitlistsTags = 'waitlists-tags',
  FormResponses = 'form-responses',
  BiDiForms = 'bidi-forms',
  Automations = 'automations',
  SpecialtyReferrals = 'specialty-ehr-referrals',
  BroadcastDeliveryStats = 'broadcast-delivery-stats',
  NewSync = 'new-sync',
  PatientFormsNewPdf = 'patient-forms-new-pdf',
  CopayDetermination = 'copay-determination',
  HubV2 = 'hub-v2',
  PSV2 = 'ps-v2',
  ChecklistHideOptions = 'checklist-hide-options',
  DropdownV2 = 'dropdown-v2',
  EmbedsCleanup = 'embeds-cleanup',
  // Rollouts to migrate the web-app into nexty (TODO):
  NextyNext = 'nexty-next',
  NextyFollowupWorkflows = 'nexty-followup-workflows',
  // Rollouts to migrate the patient-web-app into nexty-patient (TODO):
  NextyPatientFeedback = 'nexty-patient-feedback',
  NextyPatientInsurances = 'nexty-patient-insurances',
  NextyPatientSignup = 'nexty-patient-signup',
  // Rollouts to migrate the patient-web-app into nexty-patient (WIP):
  NextyPatient = 'nexty-patient',
  NextyPatientAvailabilitySelector = 'nexty-patient-availability-selector',
  NextyPatientAppointments = 'nexty-patient-appointments',
  NextyPatientChat = 'nexty-patient-chat',
  NextyPatientChecklists = 'nexty-patient-checklists',
  NextyPatientHeader = 'nexty-patient-header',
  NextyPatientLogin = 'nexty-patient-login',
  NextyPatientLumaBot = 'nexty-patient-luma-bot',
  NextyPatientPayments = 'nexty-patient-payments',
  NextyPatientSessionIdle = 'nexty-patient-session-idle',
  NextyPatientSnackbars = 'nexty-patient-snackbars',
  // Rollouts to migrate the patient-web-app into nexty (DONE):
  // NextyPatientForms = 'nexty-patient-forms',
  SparkMonitoring = 'spark-monitoring',
}

export type Address = {
  country?: string;
  address?: string;
  city?: string;
  state?: string;
  postcode?: string;
  value?: string;
};

export type MessageTemplate = EntityBase & {
  value: string;
};

export enum UserContactType {
  Inapp = 'inapp',
  Sms = 'sms',
  Voice = 'voice',
  Email = 'email',
  Fax = 'fax',
  Whatsapp = 'whatsapp',
}

export enum UserDisplayStatus {
  Active = 'active',
  Pending = 'pending',
  Suspended = 'suspended',
}

export enum UserContactArchivedReasonEnum {
  None = 'none',
  Unreachable = 'unreachable',
}

export type UserContact = {
  type?: UserContactType;
  value: string;
  active: boolean;
  archived?: boolean;
  archivedReason?: UserContactArchivedReasonEnum;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/user.js
export type UserBase = Entity & {
  active: number;
  avatar: EntityID;
  balance?: number;
  contact: UserContact[];
  country: string;
  doNotContact: boolean;
  directMessagingEmail?: string;
  email: string;
  firstname: string;
  alternativeName: string;
  gender: string;
  impersonate?: { root: string; _id: string }[];
  birthSex?: string;
  groups: string[];
  isReferringProvider: boolean;
  isRenderingProvider: boolean;
  language: string;
  lastLogin: any;
  lastname: string;
  ldapUser: boolean;
  master: boolean;
  name: string;
  roles: Role[];
  safeId?: string;
  salesforceData: {
    lifeline: 'trial' | 'converted' | 'active' | 'churn';
    respectProvisioning: boolean;
    provisioning?: ProvisioningEnum[];
    // backend
    mrr?: number;
    renewalDate?: any;
    providers?: number;
    specialty?: string;
    referralTrialLimit?: number;
    recordType?: string;
    domesticAccessRequired?: boolean;
    boardSegment?: string;
    healthSystem?: string;
    arr?: number;
    healthStatus?: string;
  };
  setting: string; // TODO Setting schema?
  settings: any;
  stats: { oldestAppointment: string };
  twoFactorAuthSecret: {
    key: string;
    period: number;
    enabled: boolean;
    backupCodes: string[];
  };
  type: 'doctor' | 'staff' | 'patient';
  visible: boolean;
  salesforceId?: string;
  rollouts: RolloutEnum[];
  // backend
  middlename?: string;
  password?: string;
  passwordHashAlgorithm?: 'md5' | 'sha256' | 'sha512' | 'bcrypt';
  passwordUpdatedAt?: any;
  resetPasswordToken?: string;
  resetPasswordExpires?: any;
  organization?: any;
  doNotContactMessage?: any;
  stripeCustomerId?: any;
  token?: string;
  externalId?: {
    source: string;
    value: string;
  };
  secondaryExternalId?: {
    source: string;
    value: string;
  };
  stripeSubscriptionId?: string;
  displayStatus?: UserDisplayStatus;
  // TODO finish typing UserBase
  // extended
  $isWidgetUser: boolean;
  telehealthVendors?: {
    zoom?: {
      id?: string;
    };
  };
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/provider.js
export type Provider = UserBase & {
  _id: string;
  description?: string;
  schedulable: boolean;
  biography: string;
  biographyUrl: string;
  headshot: any;
  source: 'upload' | 'patient-referral-form' | 'ui' | 'integrator';
  // backend
  telehealthVendors?: {
    zoom?: {
      id?: string;
      status: 'active' | 'inactive' | 'pending';
    };
  };
  deptCode?: string;
  phonetic?: string;
  npi?: number;
  // extended
  $srcAvatar: string;
  specialInstructionsTemplates?: Record<string, string>;
  specialInstructions?: string;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/reminder.js
export type Reminder = Entity & {
  appointment: string;
  status: string;
  type: string;
  statusReason?: string;
  message?: any;
  // TODO finish typing Reminder
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/facility.js
export type Facility = Entity &
  Address & {
    name: string;
    country: string;
    phone: string;
    setting: string;
    settings: any;
    visible: boolean;
    // TODO finish typing Facility
    // extended
    $address: string;
  };

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/appointment-type.js
export type AppointmentType = Entity & {
  description: string;
  name: string;
  setting: string;
  settings: any;
  visible: boolean;
  modality: 'office-visit' | 'telehealth';
  alternativeName?: string;
  // TODO finish typing AppointmentType
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/message.js
export type Message = Entity & {
  key?: string;
  from?: string;
  to?: string;
  type: 'inbound' | 'outbound';
  ref:
    | 'reminder'
    | 'waitlist'
    | 'offer'
    | 'message'
    | 'chatActivity'
    | 'feedback'
    | 'appointment'
    | 'referral'
    | 'broadcast'
    | 'generic'
    | 'stop'
    | 'patient';
  refId: {
    type: any;
  };
  status?: 'sent' | 'pending' | 'delivered' | 'undelivered' | 'opened';
  statusReason?: string;
  externalStatus: {
    code: string;
    reason: string;
  };
  channel: 'sms' | 'email' | 'voice' | 'inapp' | 'chat' | 'fax';
  text: string;
  classification?: 'positive' | 'negative' | 'irregular' | 'recognized-keywords' | 'unclassified';
  externalId: {
    source: 'mandrill' | 'twilio' | 'nexmo' | 'sendgrid' | 'plivo' | 'bandwidth';
    value: string;
  };
  secure: boolean;
  automated?: boolean;
  visibility: 'public' | 'private' | 'internal';
  file?: any;
  media: {
    url?: string;
    contentType?: string;
  };
  stats: {
    timers: {
      elapsedReplyTime?: number;
    };
  };
  retry: {
    originalMessage?: Message;
    count?: number;
  };
  terminatingStateAt: string;
  deleted: {
    type: Number;
    required: false;
    default: 0;
  };
  mentions: {
    ref: 'user' | 'group';
    refId: any;
    mention?: string;
  }[];
  recipientType?: 'doctor' | 'staff' | 'patient';
  // lookups
  outboundMessage?: Message;
  patient: Patient;
  recipient?: UserBase;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/chat-activity.js
export type ChatActivity = Entity & {
  unreadMessages: number;
  status?: 'pending' | 'closed' | 'assigned' | 'unassigned';
  reason?: 'no-further-action' | 'not-relevant' | 'spam' | 'offensive' | 'not-actionable' | 'other';
  description?: string;
  // lookups
  assignee?: UserBase;
  facilities?: Facility[];
  lastMessage: Message;
  patient: Patient;
  previousAssignee?: UserBase;
  to?: UserBase;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/patient.js
export type Patient = UserBase & {
  active: number;
  country: string;
  dateOfBirth: { year: number; month: number; day: number };
  doNotContact: boolean;
  firstname: string;
  lastname: string;
  ldapUser: boolean;
  name: string;
  roles: string[];
  safeId: string;
  salesforceData: { lifeline: string; respectProvisioning: boolean; provisioning: string[] };
  type: string;
  address?: string;
  ethnicityGroup?: string;
  phone?: string;
  previousAddress?: string;
  race?: string;
  suffix?: string;
  source?: 'widget' | 'integrator' | 'ui' | 'referring-provider';
  primaryCareProvider?: Provider;
  // TODO finish typing Patient
  // extended
  $dateOfBirth: string;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/procedure.ts
export type Procedure = UserBase & {
  patient: Patient;
  cpt?: string;
  date?: Date;
  appointment?: Appointment;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/setting.js#L119
export type SettingsScheduler = {
  availabilityBlocks: [];
  botResponses: { enabled: boolean };
  createPatientOnLookupFailure: boolean;
  hoursAheadToShowAvailability: number;
  enabled: boolean;
  forceAppointmentTypeDuration: boolean;
  maxAvailablitiesToRender: number;
  maxDaysToShow: number;
  minimumIntervalRequiredForBooking: number;
  patientAddress: { required: boolean; visible: boolean };
  providerAvailabilityFilter: [];
  reschedulerRespectsSchedulerRules: boolean;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/appointment.js#L6
export type Appointment = Entity & {
  date: string;
  duration: number;
  endDate: string;
  facility: Facility;
  integratorUpdateResults: { status: string };
  patient: Patient;
  provider: Provider;
  source: string;
  stats: { counters: { remindersSent: number } };
  status: string;
  statusReason?: string;
  type: AppointmentType;
  // extended
  $date: string;
};

// TODO add types for `Activity` and `ActivityContext`
export type Activity = EntityBase & {
  [key: string]: any;
};

export type ActivityContext = EntityBase & {
  [key: string]: any;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/availability.js#L5
export type Availability = Entity & {
  date: string;
  duration: number;
  source: string;
  endDate: string;
  status:
    | 'available'
    | 'booked-by-integrator'
    | 'booked-by-offer'
    | 'booked-pending'
    | 'booked'
    | 'expired'
    | 'offered';
  filters: any;
  facility: Facility;
  provider: Provider;
  appointmentType: AppointmentType;
  createdFromAppointment?: Appointment;
  usedForAppointment?: Appointment;
  // extended
  $date: string;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/file-upload.js#L4
export type FileUpload = Entity & {
  bucket?: string;
  awsId?: string;
  extension: string;
  contentType: string;
  previewLines: Record<string, string>[];
  allowedSquigglies: string[];
  // extended
  $allowedSquigglies: string[];
};

export enum PatientRelationship {
  Spouse = 'spouse',
  GrandfatherOrGrandmother = 'grandfather-or-grandmother',
  GrandsonOrGrandaughter = 'grandson-or-grandaughter',
  NephewOrNiece = 'nephew-or-niece',
  FosterChild = 'foster-child',
  WardOfTheCourt = 'ward-of-the-court',
  StepsonOrStepdaughter = 'stepson-or-stepdaughter',
  Self = 'self',
  Child = 'child',
  Employee = 'employee',
  Unknown = 'unknown',
  HandicappedDependent = 'handicapped-dependent',
  SponsoredDependent = 'sponsored-dependent',
  DependentOfMinorDependent = 'dependent-of-minor-dependent',
  SignificantOther = 'significant-other',
  Mother = 'mother',
  EmancipatedMinor = 'emancipated-minor',
  OrganDonor = 'organ-donor',
  CadaverDonor = 'cadaver-donor',
  InjuredPlaintiff = 'injured-plaintiff',
  ChildWhereInsuredHasNoFinancialResponsibility = 'child-where-insured-has-no-financial-responsibility',
  LifePartner = 'life-partner',
  OtherRelationship = 'other-relationship',
}

export enum EPatientRelationship {
  Self = 'self',
  Spouse = '01',
  Child = '19',
  OtherAdult = '34',
}

export type DOB = {
  year: number;
  month: number;
  day: number;
};

export type Dependent = {
  firstname: string;
  lastname: string;
  dateOfBirth: DOB;
  relationshipToSubscriber: EPatientRelationship;
  address: string;
};

export enum EEligibilityStatus {
  Active = 'active',
  Unknown = 'unknown',
  Inactive = 'inactive',
  Failed = 'failed',
}

export enum EInsuranceStatus {
  Archived = 'archived',
  Inactive = 'inactive',
  Active = 'active',
  Draft = 'draft',
}

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/insurance.js
export type Insurance = Entity & {
  payerName?: string;
  _id?: string;
  planName?: string;
  memberName: string;
  firstname: string;
  lastname: string;
  memberNumber?: string;
  startDate?: Date;
  endDate?: Date;
  insuranceGroupName?: string;
  url?: string;
  urlBack?: string;
  fileUpload?: any;
  fileUploadBack?: any;
  active: boolean;
  status?: 'archived' | 'active' | 'draft';
  relationshipToSubscriber?: {
    code?: EPatientRelationship;
    label?: string;
    value?: string;
  };
  externalId: {
    source?: string;
    value?: string;
  };
  payerId?: string;
  verification?: {
    status?: EEligibilityStatus;
    updatedAt?: number;
    requestedBy?: UserBase;
    lookupResults?: any;
  };
  // lookups
  patient?: Patient;
  // Dependent
  dependent?: Dependent;
  primary?: boolean;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/referral.js
export type Referral = Entity & {
  referringProvider?: string;
  source: 'upload' | 'patient-referral-form' | 'ui' | 'integrator';
  status:
    | 'active' // starts here when the object is created
    // when the patient calls -- when in the called
    // state, we are still texting them, basically
    // the same as `active
    | 'called'
    | 'called-late'
    // when the front desk marks the referral as
    // the patient scheduled an appt
    | 'scheduled'
    | 'scheduled-late'
    // exhausted number of attempts
    | 'incomplete'
    // expired
    | 'expired'
    // front desk cancelled by UI
    | 'cancelled'
    // pending when referral is created by a referring provider
    | 'pending'
    // final state, user has decided to close the referral, this can not move to any other state
    | 'closed';
  fileUploads?: any;
  startOn: number;
  expireAt: number;
  attempt: number;
  lastAttemptSentAt: {
    type: Date;
    required: false;
  };
  calledAt?: number;
  acceptedAt?: number;
  scheduledAt?: number;
  notes?: string;
  externalId: {
    source: any;
    value: string;
  };
  diagnoses?: any[];
  customerNotes?: string;
  reason?: string;
  processedReferralFollowup?: 'pending' | 'skipped' | 'failed' | 'success';
  upload?: any;
  faxUpload?: any;
  // lookups
  user: UserBase;
  facility?: Facility;
  provider?: Provider;
  schedulableProviders?: Provider[];
  referringProviderId?: Provider;
  patient: Patient;
  appointmentType?: AppointmentType;
  scheduledByAppointment?: Appointment;
  scheduledByOffer?: Offer;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/waitlist.js#L6
export type Waitlist = Entity & {
  department?: string;
  status:
    | 'waiting' // patient waiting for offer
    | 'in-flight' //  offer being processed
    | 'offered' //  offer made to patient, waiting for reply
    | 'confirming' //  offer accepted, waiting for staff to confirm / decline
    | 'accepted' // offer accepted by staff
    | 'pending' // waiting for integrator to update EHR
    | 'declined'; // offer declined by staff
  bookedBy:
    | 'provider' // staff added this
    | 'department' // only used by demo accounts
    | 'patient' // patient self added
    | 'auto'; // the system looked ahead and specuatlively created this
  futureAppointmentBestMatch?: boolean;
  duration?: number;
  holdUntil?: number;
  createdAt?: number;
  notes?: string;
  preferredDays: { type: number[] };
  preferredTime?: 'before-lunch' | 'after-lunch' | 'anytime';
  stats: {
    counters: {
      offersSent: {
        type: number;
      };
    };
  };
  // lookups
  user: UserBase;
  patient: Patient;
  provider: Provider;
  facility?: Facility;
  appointmentType: AppointmentType;
  offer?: Omit<Offer, 'waitlists'>;
  declinedOffer?: Omit<Offer, 'waitlists'>;
  createdFromAppointment?: Appointment;
  futureAppointment: Appointment;
  approvedBy?: UserBase;
  declinedBy?: UserBase;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/offer.js#L6
export type Offer = Entity & {
  user: UserBase;
  provider?: Provider;
  facility?: Facility;
  waitlists: Waitlist[];
  approvedBy?: UserBase;
  declinedBy?: UserBase;
  availability: Availability;
  createdFromAppointment: Appointment;
  appointmentType?: AppointmentType[];
  patientFound: Patient;
  offer?: Offer;
  date?: any;
  duration?: number;
  source: 'cancellation' | 'manual' | 'find-open-spots' | 'self-scheduling' | 'reschedule' | 'staff-scheduling';
  status: 'in-progress' | 'patient-found' | 'patient-not-found' | 'cancelled';
  integratorCreationResults: {
    status: 'success' | 'failure' | 'pending';
    errors?: string;
  };
  currentWaitlist: number;
  expireAt: any;
  patientFoundAt: any;
  estimatedCompletionAt: any;
  shadowAppointment?: string;
  protocol: 'serial' | 'parallel';
  // extended
  $patient: Patient;
};

/**
 * PatientFormTemplate
 *
 * TODO: Define schema on backend (now it's mixed content)
 */
export type PatientFormTemplateItemTypeKeys =
  | '2FA'
  | 'address'
  | 'date'
  | 'dropdown'
  | 'e_signature'
  | 'email'
  | 'group'
  | 'information_capture'
  | 'jump'
  | 'long_text'
  | 'medication_search'
  | 'multiple_choice'
  | 'patient_information'
  | 'pharmacy_search'
  | 'phone_number'
  | 'scheduler'
  | 'manageAppointment'
  | 'short_text'
  | 'ssn'
  | 'template_welcome'
  | 'template'
  | 'yes_no'
  | 'zipcode'
  | 'copay'
  | 'docusign_templates'
  | 'insurance_card'
  | 'insurance_eligibility'
  | 'insurance_verification'
  | 'insurance_capture'
  // IMH related questions types
  | 'imh'
  | 'time'
  | 'integer'
  | 'bool'
  | 'year'
  // CCDA related questions types
  | 'medication_list'
  | 'immunization_history'
  | 'allergy'
  | 'surgical_history'
  | 'family_history'
  | 'social_history'
  | 'book_status_component'
  | 'demographics';

export type PatientFormTemplateItemRedirectType =
  | 'externalUrl'
  | 'goTo'
  | 'goToForm'
  | 'goToPrevious'
  | 'isFinal'
  | 'none'
  | 'reschedule'
  | 'scheduler';

export type PatientFormTemplateItemAnswer = {
  id: EntityID;
  redirectType?: PatientFormTemplateItemRedirectType;
  text?: string;
  label?: string;
  score?: string;
  code?: string;
  disabled?: boolean;
  locales?: any;
  properties?: any;
};

export enum TInsuranceCardOptions {
  DEFAULT = 'DEFAULT',
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
}

export type PatientFormTemplateItem = {
  id: EntityID;
  type: PatientFormTemplateItemTypeKeys;
  title: string;
  // TODO: move optional fields into `properties`
  description?: string;
  text?: string;
  squiggly?: string;
  redirectType?: PatientFormTemplateItemRedirectType;
  onNextClick?: () => void;
  onPreviousClick?: () => void;
  properties: {
    answers?: PatientFormTemplateItemAnswer[];
    showDescription?: boolean;
    visibility?: string;
    showCustomAction?: boolean;
    scoringEnabled?: boolean;
    allowMultipleSelection?: boolean;
    allowOtherChoice?: boolean;
    filterAvailabilityBasedOnZipcode?: boolean;
    includeNoneOfAboveChoice?: boolean;
    includeInsuranceCard?: TInsuranceCardOptions;
    runInsuranceEligibility?: boolean;
    addToAppointment?: boolean;
    persistAnswer?: boolean;
    mustExist?: boolean;
    params?: any[];
    goTo?: EntityID;
    url?: string;
    template?: string;
    actionLabel?: string;
    actionHref?: string;
    script?: string;
    items?: PatientFormTemplateItem[];
    copay?: string;
    immunizationHistory?: any;
    surgicalHistory?: any;
    familyHistory?: any;
    locales?: any;
    canSkip?: boolean;
    notifyStaff?: boolean;
    needsReview?: boolean;
  };
  validations: {
    required?: boolean;
  };
};

export enum PatientFormTemplateLumaBotFlow {
  Welcome = 'welcome',
  SpeakWithStaff = 'speak-with-staff',
  SessionTimeout = 'session-timeout',
  KnockKnock = 'knock-knock',
  CulDeSac = 'cul-de-sac',
  Feedback = 'feedback',
}

export type PatientFormTemplate = Entity & {
  active: boolean;
  sendToDirectMessageEmail: boolean;
  title: string;
  type: 'intake' | 'pre-qualification' | 'ccda';
  reminder: { enabled: boolean };
  insurance: { enabled: boolean };
  externalId: { source: 'luma' | 'monkey' };
  schedulerRequestInterceptor: string;
  schedulerResponseInterceptor: string;
  tag: string;
  questionsForStaffReview?: { [key: string]: boolean };
  template: {
    version: string;
    items: PatientFormTemplateItem[];
    welcomeScreens: PatientFormTemplateItem[];
  };
  lumabot?: boolean;
  lumaBotFlow?: PatientFormTemplateLumaBotFlow;
  // extended
  $isIntake: boolean;
  $isPreQualification: boolean;
  $isCCDA: boolean;
};

/**
 * PatientForm
 *
 * TODO: Define schema on backend (now it's mixed content)
 */
export type PatientFormAnswer = PatientFormTemplateItemAnswer & {
  title?: string;
  text: string;
  selected: boolean;
};

export interface IInsuranceEligibilityResult {
  _id: string;
  planName: string;
  payerName: string;
  primary: boolean;
  copay: unknown[];
  coinsurance: unknown[];
  eligibilityStatus: EEligibilityStatus;
  hasActivePlanStatus: boolean;
  updatedAt?: number;
}

export type PatientFormQuestion = PatientFormTemplateItem & {
  answers: PatientFormAnswer[];
  insurances?: Insurance[];
  eligibilityResults?: IInsuranceEligibilityResult[];
  properties: PatientFormTemplateItem['properties'] & {
    items?: PatientFormQuestion[];
  };
  forceLabel?: string;
};

export type PatientFormRedirectConfig = Pick<PatientFormQuestion, 'type' | 'squiggly' | 'redirectType' | 'properties'>;

export enum PatientFormStatus {
  Pending = 'pending',
  Started = 'started',
  Completed = 'completed',
}

export enum PatientFormIntegratorUpdateResultsStatus {
  Success = 'success',
  Failure = 'failure',
  Pending = 'pending',
}

export type PatientFormIntegratorUpdateResults = {
  status?: PatientFormIntegratorUpdateResultsStatus;
  error?: string;
};

export enum PatientFormProcessingStatus {
  Open = 'open',
  InProgress = 'in-progress',
  Closed = 'closed',
}

export type PatientFormDirectMessageCcdaUpdateResults = {
  status?: PatientFormIntegratorUpdateResultsStatus;
  error?: string;
  requestAt?: string;
  responseAt?: string;
};

export enum PatientFormTemplateType {
  PreQualification = 'pre-qualification',
  Intake = 'intake',
  Feedback = 'feedback',
  Ccda = 'ccda',
}

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/patient-form.ts
export type PatientForm = Entity & {
  categoryNameToWritebackPDF?: string;
  writebackPDFToEhr?: boolean;
  writebackToEhr?: boolean;
  writebackOptions?: any;
  patient: Patient;
  appointment: Appointment;
  fileUpload?: FileUpload | string;
  patientFormTemplate: PatientFormTemplate;
  questionsForStaffReview?: { [key: string]: boolean };
  jumpLogic?: Record<string, unknown>;
  responses: {
    currentItemId: EntityID;
    path: string[];
    finalDisposition?: PatientFormRedirectConfig;
    pages: { questions: PatientFormQuestion[] }[];
  };
  answers?: any;
  status?: PatientFormStatus;
  // used by staff to track status of forms that are being processed
  processingStatus?: PatientFormProcessingStatus;
  processingStatusUpdatedBy?: UserBase | string;
  processingStatusUpdatedAt?: string;
  // in minutes
  duration?: number;
  // custom patient form url. Basically to identify any patient in survey monkey.
  url?: string;
  // The next 3 fields are information that exists inside the responses object but are here
  // in the root because they are necessary for building reports.
  path?: string[];
  currentItemId?: string;
  finalDisposition?: any;
  integratorUpdateResults?: PatientFormIntegratorUpdateResults;
  // keep track of ccda direct message status so we don't push the same forms twice
  directMessageCcdaUpdateResults?: PatientFormDirectMessageCcdaUpdateResults;
  // any calc'ed out results from the form
  calculationResult?: any;
  // when this form resulted in a list of availabilites, this is the snapshot
  // of availabiliteis rendered first
  availabilities: Availability[];
  // This field will contain the template type so it can be used in reports.
  // This is copied in by the controller from the patientFormTemplate when a new form is created
  patientFormTemplateType?: PatientFormTemplateType;
  // This field will contain the template name so it can be used in reports.
  // This is copied in by the controller from the patientFormTemplate when a new form is created
  patientFormTemplateName?: string;
  // This field will contain the name of where the form was generated from (ex. reminder, chat)
  // TODO: fix typing here once message-refs is migrated to TS
  ref: string;
  // This field will contain the id of the ref that generated the form
  refId?: string;
  // This field will contain the id of the offer that was created because of this form
  offer?: Offer | string;
  // Store the language patient filled the form
  language?: Language;
  // We need to store this as facilities because facility uses default filter and current users
  // complained that they do not want to filter this globlaly so we are adding a new setting
  // that let them choose to filter or not based on facility
  facilities?: Facility[];
  // Date patient completed the form
  completedAt?: string;
  lumabot?: boolean;
  // any querystring parameters that were used within the patient
  // application to create the form
  querystring?: ParsedQuery<string>;
  // previousPatientForm and nextPatientForm will be used first as a test for CHN
  // forms will be able to jump to another template so we can reduce the number of
  // unecessary questions in a single form which is causing a heavy issue with performance
  // previousPatientForm: patientForm where user comes from, whenever a new multi template
  // form is created and the jump template is called we create a new patientForm and set
  // the previous form here
  previousPatientForm?: EntityID;
  // nextPatientForm: the next patientForm user went, whenever the jump template is called a new multi template
  // form is created and then we update the original form with the next one they should go
  nextPatientForm?: EntityID;
  // IP address where the patient has fill out the form from
  accessedFrom?: string;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/diagnosis.ts
export type Diagnosis = UserBase & {
  patient: Patient;
  icd9?: string;
  icd10?: string;
  description?: string;
  date?: Date;
  endDate?: Date;
  appointment?: Appointment;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/patient-message-template.ts
export enum PatientMessageTemplateChannel {
  Sms = 'sms',
  Email = 'email',
  Voice = 'voice',
  Whatsapp = 'whatsapp',
}

export enum PatientMessageTemplateType {
  Chat = 'chat',
  Broadcast = 'broadcast',
}

export type PatientMessageTemplate = Entity & {
  // language the override applies to
  language: Language;
  // the actual template value, in liquid format
  message: string;
  name: string;
  type: PatientMessageTemplateType;
  channel?: PatientMessageTemplateChannel;
  autoCloseChat?: boolean;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/broadcast.ts
export enum BroadcastStatus {
  Pending = 'pending', // about to be send
  Scheduled = 'scheduled', // scheduled to be sent in future
  Uploading = 'uploading', // upload processing
  Accepted = 'accepted', // message was send to patient
  Declined = 'declined', // declined by provider
  Expired = 'expired', // date expired
  Failed = 'failed', // failed for some reason,
  Skipped = 'skipped', // system skipped sending the broadcast
  PendingCustomerSuccess = 'pending-customer-success', // broadcast is awaiting customer success approval
  DeclinedCustomerSuccess = 'declined-customer-success', // broadcast has been declined by customer success
}

export enum BroadcastProcessingStatus {
  UploadPending = 'upload-pending', // file upload to s3 in progress
  Uploaded = 'uploaded', // file uploaded to s3
  PatientsInProgress = 'patients-in-progress', // patient creation in progress
  PatientsCreated = 'patients-created', // patient created before messages being sent
  MessagesInProgress = 'messages-in-progress', // messages being sent
  Success = 'success', // broadcast sent successfully
  PartialFailure = 'partial-failure', // broadcast failed to send to one or more patients
  Failed = 'failed',
  NeedsMapping = 'needs-mapping', // sftp broadcast is created, but needs mapping and configuration
  NeedsConfiguration = 'needs-configuration', // sftp broadcast is created, has the mapping, but needs configuration
}

enum BroadcastSelectMode {
  All = 'select-all', // select all button was used when creating the broadcast
  Individual = 'select-individual', // individual patients were picked for the broadcast
}

export type BroadcastMessageOverride = {
  text: string;
  language: string;
  channel: UserContactType | 'all';
  subject: string;
  // NOTE: These don't exist yet in the model-repository
  // https://github.com/lumahealthhq/model-repository/pull/2874#discussion_r584603352
  patientMessageTemplate?: PatientMessageTemplate;
  secureChat?: boolean;
};

export type Broadcast = Entity & {
  _id?: string;
  message: string;
  filterResults: any[];
  deletedAt?: Date;
  messageOverrides?: BroadcastMessageOverride[];
  sendAt?: Date | Moment;
  status?: BroadcastStatus;
  statusReason?: string;
  selectMode?: BroadcastSelectMode; // Used only with scheduled broadcasts for debugging purposes
  uiNumberOfPatients?: number; // # of patients that the UI shows it will broadcast to. Used only with schdeuled broadcasts for debugging porpuses
  processingStatus?: BroadcastProcessingStatus;
  postApprovalStatus?: BroadcastStatus;
  messagesCreated: number;
  messagesSkipped: number;
  totalPatients: number;
  declinedReason?: string;
  upload?: any;
  title?: string;
  filters?: any;
  secureChat?: boolean;
  shouldUseUploadedContacts?: boolean;
  shouldUsePrimaryContactOnly?: boolean;
  isBeingProcessedInBatches?: boolean;
  // lookups
  approvedBy?: UserBase;
  createdFromBroadcast?: Broadcast;
  declinedBy?: UserBase;
  deletedBy?: UserBase;
  fileUpload?: FileUpload;
  patientMessageTemplate?: PatientMessageTemplate | EntityID;
  patients: Patient[];
  patientsSkipped?: Patient[];
  // extended
  _patientContactPreferences: 'luma' | 'file' | 'filePrimary';
  _timing: 'now' | 'later';
  isSftp?: boolean;
  campaign?: string;
  configuration?: {
    throttling?: {
      enabled?: boolean;
      value?: number;
      interval?: {
        value?: number;
        unit?: 'minutes' | 'hours';
      };
    };
  };
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/file-mapping.ts
export enum FileMappingType {
  Appointment = 'appointment',
  AppointmentNoSync = 'appointment-no-sync',
  Availability = 'availability',
  Patient = 'patient',
  Referral = 'referral',
  Recall = 'recall',
  Waitlist = 'waitlist',
}

export type FileMapping = Entity & {
  integrator: ExternalSource;
  type: FileMappingType;
  mapping: any;
};

// https://github.com/lumahealthhq/model-repository/blob/master/src/luma/models/upload.ts
export enum UploadStatus {
  Unmapped = 'unmapped',
  Pending = 'pending', // file has been uploaded
  Processing = 'processing', // a job is working on it
  Completed = 'completed', // file has been succesfully processed
  Failed = 'failed', // file has failed
}

export enum UploadType {
  // a list of appointments that should be synced between the appointments
  // inside of luma and presented in the file. when in this mode, a true
  // sync is performed between both the file and the database
  Appointment = 'appointment',
  // a list of appointments to import. all appointments present in the file
  // will get imported and no data inside of luma will be updated or deleted
  AppointmentNoSync = 'appointment-no-sync',
  Availability = 'availability',
  // use in patient uploads and also broadcast uploads
  Patient = 'patient',
  Referral = 'referral',
  Recall = 'recall',
  Waitlist = 'waitlist',
}

export type Upload = Entity & {
  integrator: ExternalSource;
  filename?: string;
  type: UploadType;
  mapping?: FileMapping;
  content?: any;
  status: UploadStatus;
  // bunch of optional fields for describing interesting things about the results
  results?: any;
  // human readable version
  resultsDescription?: string;
  deletedBy?: UserBase;
  deletedAt?: Date;
  incrRecordsCreated?: number;
  incrRecordsSkipped?: number;
  totalPatients?: number;
};

enum GroupType {
  User = 'user',
  System = 'system',
}

export type GroupBase = Entity & {
  name: string;
  type?: GroupType;
  users?: UserBase[];
  facilities?: Facility[];
};
